import { apiEndpoints } from '@config/env';
import { getData } from '../utils/global';
import { debug, warn, error } from '../utils/console';
import { sourcesStore } from '../store/sourcesStore';

export interface RedisDataPoint {
  timestamp: number;
  value: any;
}

export interface MergedDataPoint {
  timestamp: number;
  Datetime: Date | string; // Date object (preferred) or ISO string (for compatibility)
  source_id: number;
  source_name?: string;
  lat?: number;
  lng?: number;
  hdg?: number;
  cog?: number;
  sog?: number;
  [key: string]: any; // Other channel values
}

/**
 * Service for fetching live history over REST (`/api/stream/sources/:name/data[/merged]`).
 *
 * NOT Redis, despite the `*FromRedis` names still scattered through this layer and `streamingStore`.
 * Redis was retired in Phase D: the endpoints are served from the stream server's in-memory
 * recentStore, falling back to durable Parquet via server_file for ranges it no longer covers. The
 * response shape is unchanged from the Redis era, which is why the old names stuck.
 */
class StreamingDataService {
  /**
   * Fetch data for a specific source and channel
   * Accepts either source_id (number) or source_name (string)
   */
  async fetchChannelData(
    sourceIdOrName: number | string,
    channel: string,
    startTime?: number,
    endTime?: number
  ): Promise<RedisDataPoint[]> {
    try {
      // Convert source_id to source_name if needed
      let sourceName: string;
      if (typeof sourceIdOrName === 'number') {
        const mappedName = sourcesStore.getSourceName(sourceIdOrName);
        if (!mappedName) {
          warn(`[StreamingDataService] Could not map source_id ${sourceIdOrName} to source_name, using as string`);
          sourceName = String(sourceIdOrName);
        } else {
          sourceName = mappedName;
        }
      } else {
        sourceName = sourceIdOrName;
      }

      const url = apiEndpoints.stream.sourceData(sourceName);
      const params = new URLSearchParams();
      params.set('channel', channel);
      if (startTime) {
        params.set('startTime', startTime.toString());
      }
      if (endTime) {
        params.set('endTime', endTime.toString());
      }

      const fullUrl = `${url}?${params.toString()}`;
      const response = await getData(fullUrl);
      
      // For streaming endpoints, network failures and empty data are expected
      // Use debug level since this is normal behavior (not an error)
      if (!response.success) {
        debug(`[StreamingDataService] No data available for channel ${channel} on source ${sourceName} (expected when no history is retained for that window)`);
      }
      
      if (response.success && response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      // Empty data is normal - return empty array silently
      return [];
    } catch (err) {
      // Network errors for streaming endpoints are expected - log as warning, not error
      warn(`[StreamingDataService] Network error fetching channel ${channel} for source ${sourceIdOrName} (expected when the history endpoint is unavailable)`);
      return [];
    }
  }

  /**
   * Fetch multiple channels for a source and merge into unified data points
   * Accepts either source_id (number) or source_name (string)
   */
  /**
   * One HTTP request per source for all channels at once (falls back to per-channel fetches).
   */
  private async fetchMergedDataViaMergedEndpoint(
    sourceName: string,
    sourceId: number,
    startTime?: number,
    endTime?: number,
    channels?: string[],
    signal?: AbortSignal
  ): Promise<MergedDataPoint[] | null> {
    try {
      const url = apiEndpoints.stream.sourceDataMerged(sourceName);
      const params = new URLSearchParams();
      if (startTime !== undefined && startTime !== null) {
        params.set('startTime', String(startTime));
      }
      if (endTime !== undefined && endTime !== null) {
        params.set('endTime', String(endTime));
      }
      // Server-side projection (`?channels=`): only set when the caller asked for it — the default
      // merged read deliberately returns every channel, and most callers depend on that.
      if (channels && channels.length > 0) {
        params.set('channels', channels.join(','));
      }
      const fullUrl = params.size ? `${url}?${params.toString()}` : url;
      const response = await getData(fullUrl, signal);
      if (!response.success || !response.data || !Array.isArray(response.data.data)) {
        return null;
      }
      const raw = response.data.data as Record<string, unknown>[];
      const mergedPoints: MergedDataPoint[] = raw.map((row) => {
        const ts = typeof row.timestamp === 'number' ? row.timestamp : Number(row.timestamp);
        let dt: Date;
        if (row.Datetime instanceof Date) {
          dt = row.Datetime;
        } else if (row.datetime instanceof Date) {
          dt = row.datetime;
        } else if (typeof row.Datetime === 'string' || typeof row.datetime === 'string') {
          dt = new Date((row.Datetime ?? row.datetime) as string);
        } else {
          dt = new Date(Number.isFinite(ts) ? ts : 0);
        }
        const point: MergedDataPoint = {
          ...row,
          timestamp: Number.isFinite(ts) ? ts : dt.getTime(),
          Datetime: dt,
          source_id: sourceId,
          source_name: sourceName
        } as MergedDataPoint;
        if (!point.datetime) {
          point.datetime = dt;
        }
        return point;
      });
      mergedPoints.sort((a, b) => a.timestamp - b.timestamp);
      return mergedPoints;
    } catch {
      return null;
    }
  }

  async fetchMergedData(
    sourceIdOrName: number | string,
    channels: string[],
    startTime?: number,
    endTime?: number
  ): Promise<MergedDataPoint[]> {
    try {
      // Convert source_id to source_name if needed, and get source_id for the result
      let sourceName: string;
      let sourceId: number;
      
      if (typeof sourceIdOrName === 'number') {
        sourceId = sourceIdOrName;
        const mappedName = sourcesStore.getSourceName(sourceId);
        if (!mappedName) {
          warn(`[StreamingDataService] Could not map source_id ${sourceId} to source_name, using as string`);
          sourceName = String(sourceId);
        } else {
          sourceName = mappedName;
        }
      } else {
        sourceName = sourceIdOrName;
        const mappedId = sourcesStore.getSourceId(sourceName);
        if (!mappedId) {
          warn(`[StreamingDataService] Could not map source_name ${sourceName} to source_id, using 0`);
          sourceId = 0;
        } else {
          sourceId = mappedId;
        }
      }

      const mergedFast = await this.fetchMergedDataViaMergedEndpoint(
        sourceName,
        sourceId,
        startTime,
        endTime
      );
      if (mergedFast !== null) {
        return mergedFast;
      }

      debug('[StreamingDataService] Merged endpoint unavailable or empty, falling back to per-channel fetches', {
        sourceName
      });

      // Fetch all channels in parallel
      const channelPromises = channels.map(channel =>
        this.fetchChannelData(sourceName, channel, startTime, endTime)
      );

      const channelDataArrays = await Promise.all(channelPromises);

      // Empty is normal: the requested window may simply predate anything the server still holds.
      const totalChannelPoints = channelDataArrays.reduce((sum, arr) => sum + arr.length, 0);

      // Create a map of timestamp -> data point
      const pointsMap = new Map<number, MergedDataPoint>();

      // Merge all channel data by timestamp
      for (let i = 0; i < channels.length; i++) {
        const channel = channels[i];
        const dataPoints = channelDataArrays[i];

        for (const point of dataPoints) {
          const timestamp = point.timestamp;
          
          if (!pointsMap.has(timestamp)) {
            pointsMap.set(timestamp, {
              timestamp,
              Datetime: new Date(timestamp), // Store as Date object, not ISO string (for compatibility with fleetmap)
              source_id: sourceId,
              source_name: sourceName
            });
          }

          const mergedPoint = pointsMap.get(timestamp)!;
          mergedPoint[channel] = point.value;
        }
      }

      // Convert map to array and sort by timestamp (CRITICAL: sort before returning)
      const mergedPoints = Array.from(pointsMap.values()).sort((a, b) => a.timestamp - b.timestamp);

      // Only log if merge resulted in unexpected results
      if (mergedPoints.length === 0 && totalChannelPoints > 0) {
        warn(`[StreamingDataService] Merged ${channels.length} channels but got 0 data points for source ${sourceName}`, {
          totalChannelPoints,
          channels
        });
      }
      
      return mergedPoints;
    } catch (err) {
      // Network errors for streaming endpoints are expected - log as warning, not error
      warn(`[StreamingDataService] Network error fetching merged data for source ${sourceIdOrName} (expected when the history endpoint is unavailable)`);
      return [];
    }
  }

  /**
   * Merged rows projected to `channels` only, via `?channels=` on the merged endpoint.
   *
   * Distinct from fetchMergedData on purpose: that method returns EVERY channel regardless of the
   * list it is given (its 11 callers rely on it, and fetchMapData passes lowercase names that are not
   * live channels), and it falls back to one request per channel — which would defeat the point of
   * asking for a narrow projection. Here an unavailable endpoint yields `[]`. Used by the live
   * CALIBRATION page, where a one-hour window at 10 Hz is a few MB with 12 columns and ~10x that
   * with all of them.
   */
  async fetchMergedDataProjected(
    sourceName: string,
    channels: string[],
    startTime: number,
    endTime: number,
    signal?: AbortSignal
  ): Promise<MergedDataPoint[]> {
    const sourceId = sourcesStore.getSourceId(sourceName) ?? 0;
    const rows = await this.fetchMergedDataViaMergedEndpoint(
      sourceName,
      sourceId,
      startTime,
      endTime,
      channels,
      signal
    );
    if (rows === null) {
      debug('[StreamingDataService] Projected merged read returned nothing', { sourceName, channels: channels.length });
      return [];
    }
    return rows;
  }

  /**
   * Fetch map navigation data (lat, lng, hdg, cog, sog, source_name) for a source
   */
  async fetchMapData(
    source_id: number,
    startTime?: number,
    endTime?: number
  ): Promise<MergedDataPoint[]> {
    const channels = ['lat', 'lng', 'hdg', 'cog', 'sog', 'source_name'];
    return this.fetchMergedData(source_id, channels, startTime, endTime);
  }

  /**
   * Fetch available channels for a source
   * Accepts either source_id (number) or source_name (string)
   */
  async getAvailableChannels(sourceIdOrName: number | string): Promise<string[]> {
    try {
      // Convert source_id to source_name if needed
      let sourceName: string;
      if (typeof sourceIdOrName === 'number') {
        const mappedName = sourcesStore.getSourceName(sourceIdOrName);
        if (!mappedName) {
          warn(`[StreamingDataService] Could not map source_id ${sourceIdOrName} to source_name, using as string`);
          sourceName = String(sourceIdOrName);
        } else {
          sourceName = mappedName;
        }
      } else {
        sourceName = sourceIdOrName;
      }

      const url = apiEndpoints.stream.sourceChannels(sourceName);
      const response = await getData(url);
      
      if (response.success && response.data && Array.isArray(response.data.channels)) {
        return response.data.channels;
      }

      return [];
    } catch (err) {
      // Network errors for streaming endpoints are expected - log as warning, not error
      warn(`[StreamingDataService] Network error fetching channels for source ${sourceIdOrName} (expected when the history endpoint is unavailable)`);
      return [];
    }
  }

  /**
   * Fetch data for multiple sources in parallel
   * Accepts either source_ids (number[]) or source_names (string[])
   */
  async fetchMultipleSources(
    sourceIdsOrNames: number[] | string[],
    channels: string[],
    startTime?: number,
    endTime?: number
  ): Promise<Map<number, MergedDataPoint[]>> {
    const results = new Map<number, MergedDataPoint[]>();

    // Fetch all sources in parallel
    const promises = sourceIdsOrNames.map(async (sourceIdOrName) => {
      const data = await this.fetchMergedData(sourceIdOrName, channels, startTime, endTime);
      // Extract source_id from the first data point (if available) or map from source_name
      let sourceId: number;
      if (typeof sourceIdOrName === 'number') {
        sourceId = sourceIdOrName;
      } else {
        const mappedId = sourcesStore.getSourceId(sourceIdOrName);
        sourceId = mappedId || 0; // Fallback to 0 if mapping fails
      }
      return { sourceId, data };
    });

    const resultsArray = await Promise.all(promises);

    for (const { sourceId, data } of resultsArray) {
      results.set(sourceId, data);
    }

    return results;
  }
}

// Singleton instance
export const streamingDataService = new StreamingDataService();

